import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Combobox } from '@opendoor/atoms/combobox';
import type { ComboboxOption } from '@opendoor/atoms/combobox';
import { Text } from '@opendoor/atoms/text';
import type { SavedSearchFieldsFragment } from '__generated__/athena';
import { MapLocationSearch } from 'components/marketplace/map/LocationSearch';
import type { LocationSearchProps } from 'components/marketplace/map/LocationSearch';
import { SAVED_SEARCHES_ENABLED } from 'components/marketplace/savedSearches/featureFlag';
import { SavedSearchesDropdown } from 'components/marketplace/savedSearches/SavedSearchesDropdown';

interface QuickFiltersProps {
  bathrooms: string;
  bedrooms: string;
  onFilterChange: (beds: string, baths: string) => void;
  isLoadingFilter: boolean;
  onFiltersButtonClick: () => void;
  onApplySavedSearch: (savedSearch: SavedSearchFieldsFragment) => void;
}

const COUNT_OPTIONS: ComboboxOption[] = [
  { label: '0', value: '' },
  { label: '1+', value: '1' },
  { label: '2+', value: '2' },
  { label: '3+', value: '3' },
  { label: '4+', value: '4' },
];

export const QuickFilters = ({
  bathrooms,
  bedrooms,
  onFilterChange,
  isLoadingFilter,
  setSelectedAddress,
  selectedAddress,
  setSelectedLocation,
  selectedLocation,
  onFiltersButtonClick,
  isFocused,
  onApplySavedSearch,
}: QuickFiltersProps & LocationSearchProps) => {
  const handleOnBedroomsChange = (value: string) => {
    onFilterChange(value, bathrooms);
  };

  const handleOnBathroomsChange = (value: string) => {
    onFilterChange(bedrooms, value);
  };

  return (
    <Row className="w-full justify-between gap-5 py-6">
      <Box className="grow-[2] basis-2/5">
        <MapLocationSearch
          isFocused={isFocused}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          setSelectedLocation={setSelectedLocation}
          selectedLocation={selectedLocation}
        />
      </Box>
      <Col className="hidden w-28 gap-3 lg:flex">
        <Text as="label" className="text-label-md text-od-content-primary">
          Beds
        </Text>
        <Combobox
          aria-label="Beds"
          size="sm"
          placeholder="0"
          options={COUNT_OPTIONS}
          value={COUNT_OPTIONS.find((option) => option.value === (bedrooms || '')) ?? null}
          onValueChange={(option) => handleOnBedroomsChange(option?.value ?? '')}
        />
      </Col>
      <Col className="hidden w-28 gap-3 lg:flex">
        <Text as="label" className="text-label-md text-od-content-primary">
          Baths
        </Text>
        <Combobox
          aria-label="Baths"
          size="sm"
          placeholder="0"
          options={COUNT_OPTIONS}
          value={COUNT_OPTIONS.find((option) => option.value === (bathrooms || '')) ?? null}
          onValueChange={(option) => handleOnBathroomsChange(option?.value ?? '')}
        />
      </Col>
      <Button
        analyticsName="filter-property-search"
        aria-label="Filters"
        onClick={onFiltersButtonClick}
        variant="secondary"
        loading={isLoadingFilter}
        className="self-end"
      >
        Filters
      </Button>
      {SAVED_SEARCHES_ENABLED && (
        <Box className="self-end">
          <SavedSearchesDropdown onApplySavedSearch={onApplySavedSearch} />
        </Box>
      )}
    </Row>
  );
};
